'use client';

import {
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
} from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import type { CreatorInfo } from '@ps/types/creator';
import { BehaviorSubject, Subject } from 'rxjs';

import type { AnyVideo } from '../../functions';

// Per-video context
export type VideoPlayerContextValue = {
  video: AnyVideo;
  videoRef$: BehaviorSubject<RefObject<HTMLVideoElement | null> | undefined>;
  loaded$: BehaviorSubject<boolean>;
  loading$: BehaviorSubject<boolean>;
  ready$: BehaviorSubject<boolean>;
  positionMs$: BehaviorSubject<number>;
  currentTime$: BehaviorSubject<number>;
  totalDuration$: BehaviorSubject<number>;
  muted$: BehaviorSubject<boolean>;
};

export const VideoPlayerContext = createContext<VideoPlayerContextValue | null>(
  null,
);

export function useVideoPlayerContext() {
  const ctx = useContext(VideoPlayerContext);
  if (!ctx) {
    throw new Error(
      'useVideoPlayerContext must be used within a VideoPlayerContext.Provider',
    );
  }
  return ctx;
}

// List-level context (video clips feed)
export type VideoClipsContextValue = {
  initialIndex: number;
  currentIndex$: BehaviorSubject<number>;
  indexUpdated$: Subject<number>;
  pageSize: { width: number; height: number };
  paused$: BehaviorSubject<boolean>;
  isOverlayHidden$: BehaviorSubject<boolean>;
  isMobileLayout: boolean;
  /**
   * The player's bio context — its clips belong to one creator, so a tagged
   * product opens at that creator's PDP URL (custom-domain aware). The host
   * always renders the player under a known creator (a series / clip bio page,
   * or an owner surface via `useMyPopStore`).
   */
  bioContext: BioContext;
  /**
   * The creator whose surface owns this feed, for players whose clips carry no
   * per-clip `author` to render (the welcome video is a single bare URL). When
   * present, `VideoFooter` shows the creator's name in place of the missing
   * author row. Clip feeds leave it unset — they read the name off each clip's
   * `author`.
   */
  creatorInfo?: CreatorInfo;
  autoAdvance?: boolean;
  editable?: boolean;
  /**
   * Whether the in-player Share action can produce a URL on this host. Hosts
   * that can't build a clip permalink (no owning series/course slug, e.g. the
   * standalone `/videos` library player) pass `false` so the "Share" item is
   * hidden instead of showing a no-op. Defaults to shareable when omitted, so
   * existing series/course players are unaffected.
   */
  canShare?: boolean;
  onUpdated?: (video: AnyVideo) => void;
  onDeleted?: (video: AnyVideo) => void;
  onPrev?: () => boolean;
  onNext?: () => boolean;
  /**
   * Action dispatch — the host (e.g. `VideoModal`, `SeriesVideoPlayer`,
   * the listing page-builder video widget) handles intents emitted
   * by the player (`openProfile`, `open-link`, `requireLogin`,
   * `lockedVideoTapped`, `deleteVideo`, …). When omitted, actions
   * no-op (host hasn't wired the intent yet).
   *
   * 1:1 with the legacy postMessage vocabulary — no iframe / no
   * `window.parent.postMessage`. The host either consumes the
   * intent in-tree or it doesn't.
   */
  onAction?: (data: Record<string, unknown>) => void;
};

export const VideoClipsContext = createContext<VideoClipsContextValue | null>(
  null,
);

export function useVideoClipsContext() {
  const ctx = useContext(VideoClipsContext);
  if (!ctx) {
    throw new Error(
      'useVideoClipsContext must be used within a VideoClipsContext.Provider',
    );
  }
  return ctx;
}

// Audio mute context (global)
export type AudioContextValue = {
  muted$: BehaviorSubject<boolean>;
};

export const AudioMuteContext = createContext<AudioContextValue | null>(null);

export function useAudioMuteContext() {
  const ctx = useContext(AudioMuteContext);
  if (!ctx) {
    throw new Error(
      'useAudioMuteContext must be used within an AudioMuteContext.Provider',
    );
  }
  return ctx;
}

/**
 * Dispatch an action to whatever component is hosting the player
 * (`VideoModal`, `SeriesVideoPlayer`, etc.). The host receives via
 * its `onAction` prop on `<VideoClipsProvider>`. No-ops when no
 * host is wired.
 */
export function useDispatchAction() {
  const ctx = useContext(VideoClipsContext);
  const onAction = ctx?.onAction;
  return useCallback(
    (data: Record<string, unknown>) => {
      onAction?.(data);
    },
    [onAction],
  );
}

// Helper hooks
export function useIsVideoPaused() {
  const { paused$ } = useVideoClipsContext();
  return useBehaviorSubject(paused$)[0];
}

export function useIsOverlayHidden() {
  const { isOverlayHidden$ } = useVideoClipsContext();
  return useBehaviorSubject(isOverlayHidden$)[0];
}

export function useTogglePause() {
  const { paused$ } = useVideoClipsContext();
  return useCallback(() => {
    paused$.next(!paused$.getValue());
  }, [paused$]);
}

export function useToggleOverlay() {
  const { isOverlayHidden$ } = useVideoClipsContext();
  return useCallback(() => {
    isOverlayHidden$.next(!isOverlayHidden$.getValue());
  }, [isOverlayHidden$]);
}

export function useHideOverlay() {
  const { isOverlayHidden$ } = useVideoClipsContext();
  return useCallback(
    (value = true) => {
      if (isOverlayHidden$.getValue() !== value) {
        isOverlayHidden$.next(value);
      }
    },
    [isOverlayHidden$],
  );
}

// Update video ref hook
export function useUpdateVideoRef(ref: RefObject<HTMLVideoElement | null>) {
  const { videoRef$ } = useVideoPlayerContext();
  useEffect(() => {
    videoRef$.next(ref);
    return () => {
      videoRef$.next(undefined);
    };
  }, [ref, videoRef$]);
}
